import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {EventTriggeringConditionType} from '../../../../../generated/graphql';
import {type Trigger} from '../../TriggersTable/utilTypes';
import {
  DEFAULT_SPAN_WINDOW_DURATION_SECONDS,
  initialWeaveMetricConfig,
} from './constants';
import {
  type AggregationFunction,
  type AlertCondition,
  type ComparisonOperator,
  type SourceType,
  type WeaveMetricFilter,
  type WeaveMetricThresholdConfig,
} from './types';
import {resolveMetricType} from './utils';
import {
  isIncompleteSpanCustomAttr,
  isValidThreshold,
  isValidWindowDuration,
  isValidWindowSize,
} from './validation';

export const useWeaveMetricThresholdStore =
  create<WeaveMetricThresholdConfig>()(
    devtools(
      () => ({
        ...initialWeaveMetricConfig,
      }),
      {name: 'WeaveMetricThresholdStore'}
    )
  );

const setMetricFilter = (metricFilter: Partial<WeaveMetricFilter>) => {
  useWeaveMetricThresholdStore.setState(state => ({
    metricFilter: {...state.metricFilter, ...metricFilter},
  }));
};

export const getWeaveMetricThresholdConfig = (
  trigger: Trigger
): WeaveMetricThresholdConfig | undefined => {
  if (
    !trigger.triggeringCondition.filter ||
    trigger.triggeringCondition.eventType !==
      EventTriggeringConditionType.WeaveMetricThreshold
  ) {
    return undefined;
  }

  try {
    const parsedFilter: unknown = JSON.parse(
      trigger.triggeringCondition.filter
    );

    // Wire format of the persisted weave_metric_filter JSON
    type WireFilter = {
      op_str?: string;
      scorer_ref?: string;
      metric_path?: string;
      threshold?: number;
      comparison_operator?: string;
      alert_condition?: string;
      window_size?: number;
      window_duration?: number;
      monitor_ref?: string;
      source_type?: string;
      aggregation_function?: string;
      status_match_value?: string;
      span_operation_name?: string;
      span_name?: string;
      span_agent_name?: string;
    };

    const weaveFilter = (parsedFilter as {weave_metric_filter?: WireFilter})
      .weave_metric_filter;
    if (!weaveFilter) {
      console.error('Failed to parse weave metric filter:', parsedFilter);
      return undefined;
    }

    // Recover the UI source from the persisted filter: source_type "spans"
    // takes priority, otherwise monitor_ref presence distinguishes monitor/op.
    const hasMonitorRef = Boolean(weaveFilter.monitor_ref);
    const sourceType: SourceType =
      weaveFilter.source_type === 'spans'
        ? 'SPANS'
        : hasMonitorRef
          ? 'MONITOR'
          : 'OPERATION';

    // For monitor alerts, the scorer object ref is stored in scorer_ref
    // (op_str is empty). For operation alerts, the op ref is in op_str.
    const effectiveOpStr: string = hasMonitorRef
      ? weaveFilter.scorer_ref || ''
      : weaveFilter.op_str || '';

    return {
      metricFilter: {
        opVersionRefs: effectiveOpStr ? [effectiveOpStr] : [],
        sourceType,
        alertCondition:
          (weaveFilter.alert_condition as AlertCondition) || 'THRESHOLD',
        comparisonOperator:
          (weaveFilter.comparison_operator as ComparisonOperator) ||
          'GREATER_THAN',
        metricPath: weaveFilter.metric_path || '',
        opStr: effectiveOpStr,
        threshold: weaveFilter.threshold?.toString() || '',
        windowSize: weaveFilter.window_size,
        windowDuration: weaveFilter.window_duration,
        monitorRef: weaveFilter.monitor_ref,
        // Restore the scorer's op ref so it's available for the trigger payload
        scorerOpStr: hasMonitorRef ? weaveFilter.op_str || '' : undefined,
        aggregationFunction:
          (weaveFilter.aggregation_function as AggregationFunction) || 'mean',
        statusMatchValue: weaveFilter.status_match_value,
        spanOperationName: weaveFilter.span_operation_name,
        spanName: weaveFilter.span_name,
        spanAgentName: weaveFilter.span_agent_name,
      },
      allowAutoFocus: false,
    };
  } catch (e) {
    console.error('Failed to parse weave metric threshold config:', e);
    return undefined;
  }
};

export const WeaveMetricThresholdActions = {
  initAsync: (suggestedOpStr?: string) => {
    useWeaveMetricThresholdStore.setState({
      ...initialWeaveMetricConfig,
      metricFilter: {
        ...initialWeaveMetricConfig.metricFilter,
        opStr: suggestedOpStr,
      },
      allowAutoFocus: suggestedOpStr == null,
    });
  },

  initFromTrigger: (trigger: Trigger) => {
    const config = getWeaveMetricThresholdConfig(trigger);
    if (config) {
      useWeaveMetricThresholdStore.setState(config);
    }
  },

  setDefaultMonitor: (monitorRef: string, scorerRefs: string[]) => {
    const autoSelectedScorer = scorerRefs.length > 0 ? scorerRefs[0] : '';
    useWeaveMetricThresholdStore.setState(state => ({
      ...state,
      metricFilter: {
        ...state.metricFilter,
        sourceType: 'MONITOR',
        monitorRef,
        scorerRefs,
        opStr: autoSelectedScorer,
        opVersionRefs: autoSelectedScorer ? [autoSelectedScorer] : [],
        metricPath: '',
      },
      allowAutoFocus: false,
    }));
  },

  setOpStr: (opStr: string) => {
    setMetricFilter({opStr, metricPath: ''});
  },

  setOpVersionRefs: (opVersionRefs: string[]) => {
    const opStr = opVersionRefs.length > 0 ? opVersionRefs[0] : '';
    setMetricFilter({opVersionRefs, opStr, metricPath: ''});
  },

  setMetricPath: (metricPath: string) => {
    setMetricFilter({metricPath});
  },

  setThreshold: (threshold: string) => {
    setMetricFilter({threshold});
  },

  setComparisonOperator: (comparisonOperator: ComparisonOperator) => {
    setMetricFilter({comparisonOperator});
  },

  setWindowSize: (windowSize: number) => {
    setMetricFilter({windowSize});
  },

  setWindowDuration: (windowDuration: number) => {
    setMetricFilter({windowDuration});
  },

  setAggregationFunction: (aggregationFunction: AggregationFunction) => {
    setMetricFilter({aggregationFunction});
  },

  setStatusMatchValue: (statusMatchValue: string) => {
    setMetricFilter({statusMatchValue});
  },

  setSpanOperationName: (spanOperationName: string) => {
    setMetricFilter({spanOperationName});
  },

  setSpanName: (spanName: string) => {
    setMetricFilter({spanName});
  },

  setSpanAgentName: (spanAgentName: string) => {
    setMetricFilter({spanAgentName});
  },

  setScorerOpStr: (scorerOpStr: string | undefined) => {
    setMetricFilter({scorerOpStr});
  },

  setSourceType: (sourceType: SourceType) => {
    // Switching sources clears every source-specific selection, then the new
    // source applies its own defaults.
    const cleared: Partial<WeaveMetricFilter> = {
      sourceType,
      opVersionRefs: [],
      opStr: '',
      monitorRef: undefined,
      scorerRefs: [],
      scorerOpStr: undefined,
      metricPath: '',
      spanOperationName: undefined,
      spanName: undefined,
      spanAgentName: undefined,
    };
    if (sourceType === 'SPANS') {
      // Spans alerts are project-wide and time-windowed: only the duration
      // window and mean/min/max aggregations apply.
      setMetricFilter({
        ...cleared,
        aggregationFunction: 'mean',
        windowSize: undefined,
        windowDuration: DEFAULT_SPAN_WINDOW_DURATION_SECONDS,
        statusMatchValue: undefined,
      });
    } else {
      setMetricFilter(cleared);
    }
  },

  setMonitorRef: (monitorRef: string, scorerRefs: string[]) => {
    // Store monitor ref and available scorers, auto-selecting the first one
    const autoSelectedScorer = scorerRefs.length > 0 ? scorerRefs[0] : '';
    setMetricFilter({
      monitorRef,
      scorerRefs,
      opStr: autoSelectedScorer,
      opVersionRefs: autoSelectedScorer ? [autoSelectedScorer] : [],
      metricPath: '',
    });
  },

  setScorer: (scorerRef: string) => {
    // Set the selected scorer (used for backend filtering)
    setMetricFilter({
      opStr: scorerRef,
      opVersionRefs: scorerRef ? [scorerRef] : [],
      metricPath: '',
    });
  },

  /**
   * Validates the current metric filter state.
   * Returns null if valid, or an error message if invalid.
   */
  validate: (): string | null => {
    const {metricFilter} = useWeaveMetricThresholdStore.getState();
    const isSpans = metricFilter.sourceType === 'SPANS';

    // Spans alerts evaluate the whole project, so no op/monitor is required.
    if (!isSpans && !metricFilter.opStr) {
      if (metricFilter.sourceType === 'MONITOR') {
        if (!metricFilter.monitorRef) {
          return 'Select a monitor.';
        }
        if (!metricFilter.scorerRefs || metricFilter.scorerRefs.length === 0) {
          return 'This monitor has no scorers configured.';
        }
        return 'Select a scorer.';
      }
      return 'Select an operation.';
    }
    if (!metricFilter.metricPath) {
      return 'Select a metric.';
    }
    if (isSpans && isIncompleteSpanCustomAttr(metricFilter.metricPath)) {
      return 'Enter a custom attribute key.';
    }
    if (metricFilter.threshold === undefined || metricFilter.threshold === '') {
      return 'Enter a threshold value.';
    }
    const thresholdType = resolveMetricType(
      metricFilter.sourceType,
      metricFilter.metricPath,
      undefined
    );
    if (
      !isValidThreshold(
        metricFilter.threshold,
        thresholdType,
        metricFilter.aggregationFunction
      )
    ) {
      return thresholdType !== 'float' &&
        metricFilter.aggregationFunction === 'percentage'
        ? 'Percentage must be between 0 and 100.'
        : 'Threshold must be a valid number.';
    }
    if (isSpans) {
      // Spans alerts are time-windowed only.
      return isValidWindowDuration(metricFilter.windowDuration)
        ? null
        : 'Set a valid time window.';
    }
    const hasValidWindow =
      isValidWindowSize(metricFilter.windowSize) ||
      isValidWindowDuration(metricFilter.windowDuration);
    if (!hasValidWindow) {
      return 'Set a valid window size.';
    }

    return null;
  },

  isValid: (): boolean => {
    return WeaveMetricThresholdActions.validate() === null;
  },
};
